// API Types
export interface User {
    id: string;
    email: string;
    name: string;
}

export interface TimeSlot {
    id: string;
    event_id: string;
    start_time_utc: string;
    end_time_utc: string;
    response_count?: number;
}

export interface Recipient {
    id: string;
    event_id: string;
    email: string;
    name?: string;
    invited_at?: string;
    last_responded_at?: string;
    has_responded: boolean;
}

export interface Event {
    id: string;
    creator_id: string;
    creator_email: string;
    creator_name: string;
    creator_timezone: string;
    event_name: string;
    comments?: string;
    duration_minutes: number;
    status: 'draft' | 'sent' | 'closed' | 'canceled';
    created_at: string;
    sent_at?: string;
    canceled_at?: string;
    recipient_count: number;
    response_count: number;
    time_slots?: TimeSlot[];
    recipients?: Recipient[];
}

export interface EventFormData {
    event_name: string;
    comments?: string;
    duration_minutes: number;
    timezone: string;
    time_slots: { start_time_utc: string; end_time_utc: string }[];
    recipients: string[];
}

export interface ResponseSummary {
    event_id: string;
    event_name: string;
    slot_totals: {
        slot_id: string;
        start_time_utc: string;
        end_time_utc: string;
        response_count: number;
    }[];
    recipient_breakdown: {
        recipient_email: string;
        recipient_name?: string;
        last_responded_at?: string;
        selected_times?: string;
    }[];
}

// API Response wrapper
interface ApiResponse<T> {
    success: boolean;
    message?: string;
    data?: T;
    error?: string;
    errors?: Record<string, string>;
}

// API Client
const API_BASE = '/api';

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options?.headers,
        },
        credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || data.message || 'Request failed');
    }

    return data;
}

// Auth API
export const authApi = {
    getLoginUrl: () => `${API_BASE}/auth/login`,

    getCurrentUser: async (): Promise<User | null> => {
        try {
            const res = await request<ApiResponse<{ user: User }>>('/auth/me');
            return res.data?.user ?? null;
        } catch {
            return null;
        }
    },

    logout: async (): Promise<void> => {
        window.location.href = `${API_BASE}/auth/logout`;
    },
};

// Events API
export const eventsApi = {
    list: async (status?: string): Promise<Event[]> => {
        const query = status ? `?status=${status}` : '';
        const res = await request<ApiResponse<{ events: Event[] }>>(`/events${query}`);
        return res.data?.events ?? [];
    },

    get: async (id: string): Promise<Event> => {
        const res = await request<ApiResponse<{ event: Event }>>(`/events/${id}`);
        if (!res.data?.event) throw new Error('Event not found');
        return res.data.event;
    },

    create: async (data: EventFormData): Promise<Event> => {
        const res = await request<ApiResponse<{ event: Event }>>('/events', {
            method: 'POST',
            body: JSON.stringify(data),
        });
        if (!res.data?.event) throw new Error('Failed to create event');
        return res.data.event;
    },

    update: async (id: string, data: Partial<EventFormData>): Promise<Event> => {
        const res = await request<ApiResponse<{ event: Event }>>(`/events/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
        if (!res.data?.event) throw new Error('Failed to update event');
        return res.data.event;
    },

    delete: async (id: string): Promise<void> => {
        await request(`/events/${id}`, { method: 'DELETE' });
    },

    send: async (id: string): Promise<{ sent_count: number; errors: string[] }> => {
        const res = await request<ApiResponse<{ sent_count: number; errors: string[] }>>(`/events/${id}/send`, {
            method: 'POST',
        });
        return { sent_count: res.data?.sent_count ?? 0, errors: res.data?.errors ?? [] };
    },

    cancel: async (id: string): Promise<void> => {
        await request(`/events/${id}/cancel`, { method: 'POST' });
    },

    getResponses: async (id: string): Promise<ResponseSummary> => {
        const res = await request<ApiResponse<ResponseSummary>>(`/events/${id}/responses`);
        return res.data as ResponseSummary;
    },

    addRecipient: async (id: string, email: string, name?: string): Promise<void> => {
        await request(`/events/${id}/recipients`, {
            method: 'POST',
            body: JSON.stringify({ email, name }),
        });
    },

    convertToMeeting: async (id: string, slotId: string): Promise<{ sent_count: number; meeting_time: string; errors: string[]; meeting_type?: string; teams_link?: string }> => {
        const res = await request<ApiResponse<{ sent_count: number; meeting_time: string; errors: string[]; meeting_type?: string; teams_link?: string }>>(`/events/${id}/convert`, {
            method: 'POST',
            body: JSON.stringify({ slot_id: slotId }),
        });
        return {
            sent_count: res.data?.sent_count ?? 0,
            meeting_time: res.data?.meeting_time ?? '',
            errors: res.data?.errors ?? [],
            meeting_type: res.data?.meeting_type,
            teams_link: res.data?.teams_link
        };
    },
};

// Response API (for recipients)
export const respondApi = {
    getEvent: async (token: string) => {
        const res = await request<ApiResponse<{ event: any; recipient: any }>>(`/respond/${token}`);
        return res.data;
    },

    submit: async (token: string, slotIds: string[]): Promise<void> => {
        await request(`/respond/${token}`, {
            method: 'POST',
            body: JSON.stringify({ slot_ids: slotIds }),
        });
    },
};

// Microsoft OAuth API
export const microsoftApi = {
    getLoginUrl: () => `${API_BASE}/auth/microsoft/login`,

    getStatus: async (): Promise<{ configured: boolean; connected: boolean }> => {
        try {
            const res = await request<ApiResponse<{ configured: boolean; connected: boolean }>>('/auth/microsoft/status');
            return res.data ?? { configured: false, connected: false };
        } catch {
            return { configured: false, connected: false };
        }
    },

    disconnect: async (): Promise<void> => {
        await request('/auth/microsoft/disconnect', { method: 'POST' });
    },
};

// Calendar API (Microsoft integration)
export interface CalendarEvent {
    subject: string;
    start: string;
    end: string;
    status: string;
}

export const calendarApi = {
    getAvailability: async (start: string, end: string): Promise<{ connected: boolean; events: CalendarEvent[] }> => {
        try {
            const res = await request<ApiResponse<{ connected: boolean; events: CalendarEvent[] }>>(
                `/calendar/availability?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
            );
            return res.data ?? { connected: false, events: [] };
        } catch {
            return { connected: false, events: [] };
        }
    },
};


